import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { getImageUrl } from '../utils/imageUrl';
import { formatPrice } from '../utils/format';
import SEO from '../components/SEO';
import SectionRenderer from '../components/sections/SectionRenderer';
import { HOME_SECTION_REGISTRY } from '../components/sections/homeSectionRegistry';
import { ProductCard, Button, Skeleton } from '../components/primitives';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useCatalog, tagLabel, ageGroupLabel, ageGroupParam } from '../utils/catalog';
import './Products.css';

const PRODUCTS_PER_PAGE = 12;
const TAGS_VISIBLE = 8;
const SORTS = [
  { id: 'popularity', api: 'popularity', key: 'products.sortPopularity', fallback: 'Most liked' },
  { id: 'newest', api: 'newest', key: 'products.sortNewest', fallback: 'Newest' },
  { id: 'price-low', api: 'price_asc', key: 'products.sortPriceLow', fallback: 'Price: low to high' },
  { id: 'price-high', api: 'price_desc', key: 'products.sortPriceHigh', fallback: 'Price: high to low' },
  { id: 'name', api: 'name', key: 'products.sortName', fallback: 'Name A–Z' },
];

/** "3-5" → {min: 3, max: 5}; "9+" (older links) → {min: 9, max: null}. */
const parseAge = (value) => {
  const match = /^(\d{1,2})(?:-(\d{1,2})|\+)$/.exec(value || '');
  if (!match) return null;
  return { min: Number(match[1]), max: match[2] !== undefined ? Number(match[2]) : null };
};

const readFilters = (params) => ({
  category: params.get('category') || '',
  tags: (params.get('tags') || '').split(',').filter(Boolean),
  age: params.get('age') || '',
  priceMin: params.get('priceMin') || '',
  priceMax: params.get('priceMax') || '',
  inStock: params.get('inStock') === '1',
  sort: params.get('sort') || 'popularity',
  search: params.get('search') || '',
  page: Math.max(1, parseInt(params.get('page'), 10) || 1),
});

/**
 * Price range: two sliders sharing one track, committed when the shopper
 * lets go (or leaves the field), so dragging doesn't fire a request per
 * pixel.
 */
const PriceRange = ({ bounds, value, onCommit, label, lang, currencyLabel }) => {
  const floor = Math.floor(bounds.min ?? 0);
  const ceil = Math.ceil(bounds.max ?? 0);
  const step = ceil - floor > 5000 ? 100 : 50;
  const [low, setLow] = useState(value.min !== '' ? Number(value.min) : floor);
  const [high, setHigh] = useState(value.max !== '' ? Number(value.max) : ceil);
  useEffect(() => { setLow(value.min !== '' ? Number(value.min) : floor); }, [value.min, floor]);
  useEffect(() => { setHigh(value.max !== '' ? Number(value.max) : ceil); }, [value.max, ceil]);
  if (!(ceil > floor)) return null;
  const commit = () => onCommit(low <= floor ? '' : String(low), high >= ceil ? '' : String(high));
  const pct = (n) => ((n - floor) / (ceil - floor)) * 100;
  return (
    <div className="price-range">
      <div className="price-range__track" style={{ '--from': `${pct(low)}%`, '--to': `${pct(high)}%` }}>
        <input type="range" min={floor} max={ceil} step={step} value={low} aria-label={`${label}: min`}
          onChange={(event) => setLow(Math.min(Number(event.target.value), high - step))}
          onPointerUp={commit} onKeyUp={commit} onBlur={commit} />
        <input type="range" min={floor} max={ceil} step={step} value={high} aria-label={`${label}: max`}
          onChange={(event) => setHigh(Math.max(Number(event.target.value), low + step))}
          onPointerUp={commit} onKeyUp={commit} onBlur={commit} />
      </div>
      <div className="price-range__values">
        <span>{formatPrice(low, { lang })} {currencyLabel}</span>
        <span>{formatPrice(high, { lang })} {currencyLabel}</span>
      </div>
    </div>
  );
};

const Products = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { featureAgeRecommendations, currency, currencySymbol } = useSiteSettings();
  const lang = (i18n.language || 'sq').split('-')[0];
  const currencyLabel = currencySymbol || currency || '';

  // The URL is the single source of truth: header links, "shop by age"
  // tiles and back/forward all just change it.
  const filters = readFilters(searchParams);
  const { categories, facets } = useCatalog(lang, filters.category);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchLayout, setSearchLayout] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const isEditorPreview = new URLSearchParams(window.location.search).get('themePreview') === '1';
  const isSearchTemplate = Boolean(filters.search) || isEditorPreview;

  useEffect(() => {
    if (!isSearchTemplate) return undefined;
    let active = true;
    api.get('/api/themes/templates/search.default')
      .then((response) => { if (active) setSearchLayout(response.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [isSearchTemplate]);

  useEffect(() => {
    if (!isEditorPreview) return undefined;
    const receivePreview = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== 'STOREFRONT_LAYOUT_PREVIEW') return;
      setSearchLayout(event.data.payload);
    };
    window.addEventListener('message', receivePreview);
    window.parent?.postMessage({ type: 'STOREFRONT_LAYOUT_PREVIEW_READY' }, window.location.origin);
    return () => window.removeEventListener('message', receivePreview);
  }, [isEditorPreview]);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setFiltersOpen(false); };
    window.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [filtersOpen]);

  const queryKey = searchParams.toString();
  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams({ page: String(filters.page), limit: String(PRODUCTS_PER_PAGE), lang });
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('categorySlug', filters.category);
    if (filters.tags.length) params.set('tags', filters.tags.join(','));
    const age = parseAge(filters.age);
    if (age) {
      params.set('ageMin', String(age.min));
      if (age.max !== null) params.set('ageMax', String(age.max));
    }
    if (filters.priceMin) params.set('priceMin', filters.priceMin);
    if (filters.priceMax) params.set('priceMax', filters.priceMax);
    if (filters.inStock) params.set('inStock', '1');
    params.set('sortBy', (SORTS.find((sort) => sort.id === filters.sort) || SORTS[0]).api);
    api.get(`/api/products?${params.toString()}`)
      .then((response) => {
        if (!active) return;
        setProducts(response.data.products || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.pages || 1);
      })
      .catch((error) => console.error('Error fetching products:', error))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, lang]);

  const update = (changes, { keepPage = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      const serialized = Array.isArray(value) ? value.join(',') : (value === true ? '1' : value);
      if (!serialized || (key === 'sort' && serialized === 'popularity')) next.delete(key);
      else next.set(key, serialized);
    });
    if (!keepPage) next.delete('page');
    if (changes.category !== undefined) next.delete('tags');
    setSearchParams(next);
  };
  const toggleTag = (tag) => update({ tags: filters.tags.includes(tag) ? filters.tags.filter((x) => x !== tag) : [...filters.tags, tag] });
  const clearFilters = () => setSearchParams(filters.search ? new URLSearchParams({ search: filters.search }) : new URLSearchParams());
  const goToPage = (page) => {
    update({ page: page > 1 ? String(page) : '' }, { keepPage: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const category = categories.find((c) => c.slug === filters.category);
  const ageGroups = featureAgeRecommendations ? (facets?.ageGroups || []) : [];
  const tagFacets = facets?.tags || [];
  const visibleTags = showAllTags ? tagFacets : tagFacets.slice(0, TAGS_VISIBLE);
  const ageFromUrl = parseAge(filters.age);
  const ageText = ageFromUrl
    ? (ageFromUrl.max === null ? t('pdp.ageMinPlus', { min: ageFromUrl.min, defaultValue: `${ageFromUrl.min}+` }) : ageGroupLabel(t, ageFromUrl))
    : '';
  const priceText = (filters.priceMin || filters.priceMax)
    ? `${formatPrice(filters.priceMin || facets?.price?.min || 0, { lang })}–${formatPrice(filters.priceMax || facets?.price?.max || 0, { lang })} ${currencyLabel}`
    : '';

  const title = filters.search
    ? t('products.searchTitle', { query: filters.search, defaultValue: `Results for "${filters.search}"` })
    : category?.name
      || (filters.tags.length === 1 ? tagLabel(t, filters.tags[0]) : '')
      || (ageText && !filters.category && !filters.tags.length ? t('products.forAge', { age: ageText, defaultValue: `Toys for ${ageText}` }) : '')
      || t('products.allTitle', 'All products');

  const option = (key, selected, onClick, label, count, radio) => (
    <button key={key} type="button" className={`filter-option${selected ? ' is-selected' : ''}`} onClick={onClick} aria-pressed={selected}>
      <span className={`filter-option__mark${radio ? ' filter-option__mark--radio' : ''}`} aria-hidden="true">
        {selected && !radio && <Icon.Check size={12} strokeWidth={3} />}
      </span>
      <span className="filter-option__label">{label}</span>
      {count != null && <span className="filter-option__count">{count}</span>}
    </button>
  );

  const filterPanel = (
    <div className="filter-panel">
      {categories.length > 1 && (
        <fieldset className="filter-group">
          <legend>{t('products.categories', 'Category')}</legend>
          {option('all', !filters.category, () => update({ category: '' }), t('products.allCategories', 'All'), null, true)}
          {categories.filter((c) => c.productCount !== 0).map((c) => option(c.slug, filters.category === c.slug, () => update({ category: filters.category === c.slug ? '' : c.slug }), c.name, c.productCount, true))}
        </fieldset>
      )}

      {ageGroups.length > 0 && (
        <fieldset className="filter-group">
          <legend>{t('products.shopByAge', 'Age')}</legend>
          <div className="filter-pills">
            {ageGroups.map((group) => {
              const id = ageGroupParam(group);
              const selected = filters.age === id;
              return (
                <button key={id} type="button" className={`filter-pill${selected ? ' is-selected' : ''}`} aria-pressed={selected} onClick={() => update({ age: selected ? '' : id })}>
                  {ageGroupLabel(t, group)} <span>{group.count}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      {facets?.price?.max > facets?.price?.min && (
        <fieldset className="filter-group">
          <legend>{t('products.price', 'Price')}</legend>
          <PriceRange
            bounds={facets.price}
            value={{ min: filters.priceMin, max: filters.priceMax }}
            onCommit={(min, max) => { if (min !== filters.priceMin || max !== filters.priceMax) update({ priceMin: min, priceMax: max }); }}
            label={t('products.price', 'Price')}
            lang={lang}
            currencyLabel={currencyLabel}
          />
        </fieldset>
      )}

      {tagFacets.length > 0 && (
        <fieldset className="filter-group">
          <legend>{t('products.type', 'Type')}</legend>
          {visibleTags.map(({ tag, count }) => option(tag, filters.tags.includes(tag), () => toggleTag(tag), tagLabel(t, tag), count))}
          {tagFacets.length > TAGS_VISIBLE && (
            <button type="button" className="filter-more" onClick={() => setShowAllTags((v) => !v)}>
              {showAllTags ? t('products.showFewer', 'Show fewer') : t('products.showAllTypes', { count: tagFacets.length, defaultValue: `Show all (${tagFacets.length})` })}
            </button>
          )}
        </fieldset>
      )}

      <label className="filter-toggle">
        <span>{t('products.inStockOnly', 'In stock only')}</span>
        <input type="checkbox" role="switch" checked={filters.inStock} onChange={(event) => update({ inStock: event.target.checked })} />
        <span className="filter-toggle__track" aria-hidden="true" />
      </label>
    </div>
  );

  const chip = (key, label, onRemove) => (
    <button key={key} type="button" className="active-filter-chip" onClick={onRemove} aria-label={`${t('products.removeFilter', 'Remove filter')}: ${label}`}>
      {label}
      <Icon.Close size={13} strokeWidth={2.2} aria-hidden="true" />
    </button>
  );
  const activeChips = [
    filters.search && chip('search', `"${filters.search}"`, () => update({ search: '' })),
    category && chip('category', category.name, () => update({ category: '' })),
    ageText && chip('age', ageText, () => update({ age: '' })),
    priceText && chip('price', priceText, () => update({ priceMin: '', priceMax: '' })),
    filters.inStock && chip('stock', t('products.inStockOnly', 'In stock only'), () => update({ inStock: false })),
    ...filters.tags.map((tag) => chip(`tag-${tag}`, tagLabel(t, tag), () => toggleTag(tag))),
  ].filter(Boolean);
  const activeFilterCount = activeChips.length - (filters.search ? 1 : 0);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const current = filters.page;
    const maxVisible = 5;
    let startPage = Math.max(1, current - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) startPage = Math.max(1, endPage - maxVisible + 1);
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<button key={i} type="button" onClick={() => goToPage(i)} className={current === i ? 'active' : ''} aria-current={current === i ? 'page' : undefined}>{i}</button>);
    }
    return (
      <nav className="pagination" aria-label={t('products.pagination', 'Pagination')}>
        <button type="button" onClick={() => goToPage(current - 1)} disabled={current === 1} aria-label={t('common.previous', 'Previous page')}>
          <Icon.ChevronLeft size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
        </button>
        {startPage > 1 && <><button type="button" onClick={() => goToPage(1)}>1</button>{startPage > 2 && <span className="page-ellipsis" aria-hidden="true">…</span>}</>}
        {pages}
        {endPage < totalPages && <>{endPage < totalPages - 1 && <span className="page-ellipsis" aria-hidden="true">…</span>}<button type="button" onClick={() => goToPage(totalPages)}>{totalPages}</button></>}
        <button type="button" onClick={() => goToPage(current + 1)} disabled={current === totalPages} aria-label={t('common.next', 'Next page')}>
          <Icon.ChevronRight size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
        </button>
      </nav>
    );
  };

  const isInitialLoad = loading && products.length === 0;
  const sortSelect = (id) => (
    <label className="products-sort" htmlFor={id}>
      <Icon.Sort size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
      <span className="visually-hidden">{t('products.sortBy', 'Sort by')}</span>
      <select id={id} className="sort-select" value={filters.sort} onChange={(event) => update({ sort: event.target.value })}>
        {SORTS.map((sort) => <option key={sort.id} value={sort.id}>{t(sort.key, sort.fallback)}</option>)}
      </select>
    </label>
  );

  const searchSections = searchLayout?.template?.sections || [
    { key: 'search-main', type: 'search_main', enabled: true, position: 0, settings: {} },
  ];
  const searchMain = searchSections.find((section) => section.type === 'search_main') || searchSections[0];
  const searchRecommendations = searchSections.find((section) => section.type === 'search_recommendations' && section.enabled);
  const breadcrumbs = [
    { name: t('nav.home', 'Home'), url: '/' },
    { name: t('nav.allProducts', 'All products'), url: '/products' },
    ...(category ? [{ name: category.name, url: `/products?category=${category.slug}` }] : []),
  ];
  const seo = useMemo(() => (
    <SEO
      title={title}
      description={category?.description || t('products.metaDescription', { count: total, defaultValue: `${total} products` })}
      keywords={[title]}
      breadcrumbs={breadcrumbs}
      collection={{
        name: title,
        items: products.map((p) => ({
          name: p.name,
          slug: p.slug,
          price: p.price,
          image: (p.images && p.images[0]) ? getImageUrl(p.images[0], { size: 'large' }) : null,
        })),
      }}
    />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [title, total, products, category]);

  const searchResultsContent = (
    <div className="products-page" data-section-key={searchMain.key}>
      <div className="container">
        <nav className="breadcrumb" aria-label={t('common.breadcrumb', 'Breadcrumb')}>
          <Link to="/">{t('nav.home', 'Home')}</Link>
          <span className="breadcrumb-sep" aria-hidden="true">/</span>
          {category ? (
            <>
              <Link to="/products">{t('nav.allProducts', 'All products')}</Link>
              <span className="breadcrumb-sep" aria-hidden="true">/</span>
              <span className="breadcrumb-current">{category.name}</span>
            </>
          ) : (
            <span className="breadcrumb-current">{t('nav.allProducts', 'All products')}</span>
          )}
        </nav>

        <header className="products-header">
          <h1 className="products-title">{title}</h1>
          <p className="products-subtitle" aria-live="polite">
            {t('products.totalCount', { count: total, defaultValue: `${total} products` })}
          </p>
        </header>

        <div className="products-layout">
          <aside className="products-filters" aria-label={t('products.filters', 'Filters')}>
            {filterPanel}
          </aside>

          <div className="products-main">
            <div className="products-toolbar">
              <button type="button" className="products-filter-toggle" onClick={() => setFiltersOpen(true)} aria-haspopup="dialog" aria-expanded={filtersOpen}>
                <Icon.Filters size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                {t('products.filters', 'Filters')}
                {activeFilterCount > 0 && <span className="products-filter-count">{activeFilterCount}</span>}
              </button>
              <div className="active-filters" aria-label={t('products.activeFilters', 'Active filters')}>
                {activeChips}
                {activeChips.length > 0 && (
                  <button type="button" className="clear-all-btn" onClick={clearFilters}>{t('products.clearAll', 'Clear all')}</button>
                )}
              </div>
              {sortSelect('products-sort')}
            </div>

            {isInitialLoad ? (
              <div className="products-grid" aria-busy="true" aria-label={t('common.loading', 'Loading')}>
                {Array.from({ length: PRODUCTS_PER_PAGE }).map((_, i) => <Skeleton.Card key={i} />)}
              </div>
            ) : products.length > 0 ? (
              <>
                <div className={`products-grid${loading ? ' is-refreshing' : ''}`}>
                  {products.map((product) => <ProductCard key={product.id} product={product} />)}
                </div>
                {renderPagination()}
              </>
            ) : (
              <div className="no-products">
                <h2>{t('products.noProducts', 'No products found')}</h2>
                <p>{t('products.noProductsMessage', 'Try adjusting your search or filters.')}</p>
                <Button variant="brand" onClick={clearFilters}>{t('products.showAll', 'Show all products')}</Button>
              </div>
            )}
          </div>
        </div>

        {filtersOpen && (
          <>
            <div className="filter-sheet-scrim" onClick={() => setFiltersOpen(false)} aria-hidden="true" />
            <div className="filter-sheet" role="dialog" aria-modal="true" aria-label={t('products.filters', 'Filters')}>
              <div className="filter-sheet__grab" aria-hidden="true" />
              <div className="filter-sheet__head">
                <strong>{t('products.filters', 'Filters')}</strong>
                {activeFilterCount > 0 && <button type="button" className="clear-all-btn" onClick={clearFilters}>{t('products.clearAll', 'Clear all')}</button>}
                <button type="button" className="filter-sheet__close" onClick={() => setFiltersOpen(false)} aria-label={t('common.close', 'Close')}>
                  <Icon.Close size={18} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
              <div className="filter-sheet__body">{filterPanel}</div>
              <div className="filter-sheet__foot">
                <Button variant="brand" onClick={() => setFiltersOpen(false)}>
                  {t('products.showResults', { count: total, defaultValue: `Show ${total} products` })}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (!isSearchTemplate) return <>{seo}{searchResultsContent}</>;

  const searchRegions = [
    {
      key: searchMain.key,
      position: Number(searchMain.position) || 0,
      content: isEditorPreview ? (
        <div
          className="editor-preview-section"
          data-editor-preview-section={searchMain.key}
          onClickCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
            window.parent?.postMessage({ type: 'STOREFRONT_SECTION_SELECTED', sectionKey: searchMain.key }, window.location.origin);
          }}
        >
          {searchResultsContent}
        </div>
      ) : searchResultsContent,
    },
    ...(searchRecommendations ? [{
      key: searchRecommendations.key,
      position: Number(searchRecommendations.position) || 0,
      content: <SectionRenderer layout={{ template: { sections: [searchRecommendations] } }} registry={HOME_SECTION_REGISTRY} context={{ isEditorPreview }} />,
    }] : []),
  ].sort((left, right) => left.position - right.position);

  return <>{seo}{searchRegions.map((region) => <React.Fragment key={region.key}>{region.content}</React.Fragment>)}</>;
};

export default Products;
